import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import { useAuth } from "contexts/AuthContext";

const myHeaders = new Headers();
myHeaders.append("Authorization", "Bearer 8|os13hSpO7rDBzZqKYVCG5lgEjcOVhtsWPMKsdZcW7ea046a6");

const requestOptions = {
  method: "GET",
  headers: myHeaders,
  redirect: "follow",
};

export default function MemberDashboard() {
  const { user } = useAuth();
  const [titheData, setTitheData] = useState(null);
  const [welfareData, setWelfareData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState({
    tithe: true,
    welfare: true,
    attendance: true,
  });
  const [error, setError] = useState({
    tithe: null,
    welfare: null,
    attendance: null,
  });

  useEffect(() => {
    if (!user?.member_id) {
      console.log("No member_id found for user");
      setLoading({ tithe: false, welfare: false, attendance: false });
      return;
    }

    const fetchTitheData = async () => {
      try {
        setLoading((prev) => ({ ...prev, tithe: true }));
        const response = await fetch(
          `https://api.ofi-cia.com/api/analytics/member-tithe/${user.member_id}`,
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setTitheData(result);
        setError((prev) => ({ ...prev, tithe: null }));
      } catch (err) {
        console.error("Error fetching tithe data:", err);
        setError((prev) => ({ ...prev, tithe: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, tithe: false }));
      }
    };

    const fetchWelfareData = async () => {
      try {
        setLoading((prev) => ({ ...prev, welfare: true }));
        const response = await fetch(
          `https://api.ofi-cia.com/api/analytics/member-welfare/${user.member_id}`,
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setWelfareData(result);
        setError((prev) => ({ ...prev, welfare: null }));
      } catch (err) {
        console.error("Error fetching welfare data:", err);
        setError((prev) => ({ ...prev, welfare: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, welfare: false }));
      }
    };

    const fetchAttendanceData = async () => {
      try {
        setLoading((prev) => ({ ...prev, attendance: true }));
        const response = await fetch(
          `https://api.ofi-cia.com/api/analytics/member-attendance/${user.member_id}`,
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setAttendanceData(result);
        setError((prev) => ({ ...prev, attendance: null }));
      } catch (err) {
        console.error("Error fetching attendance data:", err);
        setError((prev) => ({ ...prev, attendance: err.message }));
      } finally {
        setLoading((prev) => ({ ...prev, attendance: false }));
      }
    };

    // Fetch all APIs concurrently
    Promise.all([fetchTitheData(), fetchWelfareData(), fetchAttendanceData()]);
  }, [user?.member_id]);

  // Reusable currency formatter with Cedi symbol
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "N/A";

    if (typeof amount === "number") {
      return `₵${amount.toLocaleString("en-GH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    return `₵${amount.toString()}`;
  };

  // Format the amount for display
  const formatAmount = (data, dataType = "tithe") => {
    if (!data) return "N/A";

    let amount;

    if (dataType === "tithe") {
      amount = data.total || data.total_tithe || data.value || data.tithe_amount;
    } else if (dataType === "welfare") {
      amount = data.amount || data.total || data.value || data.welfare_amount || data.balance;
    }

    return formatCurrency(amount);
  };

  // Format attendance count for display
  const formatAttendanceCount = (data) => {
    if (!data) return "N/A";

    const count =
      data.total_attendance || data.attendance_count || data.count || data.total || data.value;

    if (count !== undefined && count !== null) {
      if (typeof count === "number") {
        if (count <= 1 && count > 0) {
          return `${(count * 100).toFixed(1)}%`;
        }
        return count.toString();
      }
      return count.toString();
    }

    return "N/A";
  };

  // Calculate attendance percentage for the card
  const getAttendancePercentage = () => {
    if (!attendanceData) return { color: "success", amount: "+5%", label: "than last month" };

    const current = attendanceData.current_period || attendanceData.this_month || 0;
    const previous = attendanceData.previous_period || attendanceData.last_month || 0;

    if (previous > 0) {
      const change = ((current - previous) / previous) * 100;
      return {
        color: change >= 0 ? "success" : "error",
        amount: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
        label: "than last month",
      };
    }

    return {
      color: "success",
      amount: "+5%",
      label: "than last month",
    };
  };

  // Get loading status for specific card
  const isLoading = (type) => loading[type];

  // Get error status for specific card
  const hasError = (type) => error[type];

  // Show loading state while checking for user data
  if (!user) {
    return (
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="primary"
                icon="calendar_today"
                title="My Attendance"
                count="Loading..."
                percentage={{
                  color: "success",
                  amount: "+5%",
                  label: "than last month",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="volunteer_activism"
                title="My Tithe"
                count="Loading..."
                percentage={{
                  color: "success",
                  amount: "+12%",
                  label: "this year",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="support"
                title="My Welfare"
                count="Loading..."
                percentage={{
                  color: "warning",
                  amount: "+8%",
                  label: "this quarter",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="groups"
                title="My Groups"
                count="Loading..."
                percentage={{
                  color: "info",
                  amount: "",
                  label: "active participation",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
    );
  }

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="primary"
              icon="calendar_today"
              title="My Attendance"
              count={
                isLoading("attendance")
                  ? "Loading..."
                  : hasError("attendance")
                  ? "Error"
                  : formatAttendanceCount(attendanceData)
              }
              percentage={getAttendancePercentage()}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="success"
              icon="volunteer_activism"
              title="My Tithe"
              count={
                isLoading("tithe")
                  ? "Loading..."
                  : hasError("tithe")
                  ? "Error"
                  : formatAmount(titheData, "tithe")
              }
              percentage={{
                color: "success",
                amount: "+12%",
                label: "this year",
              }}
            />
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="warning"
              icon="support"
              title="My Welfare"
              count={
                isLoading("welfare")
                  ? "Loading..."
                  : hasError("welfare")
                  ? "Error"
                  : formatAmount(welfareData, "welfare")
              }
              percentage={{
                color: "warning",
                amount: "+8%",
                label: "this quarter",
              }}
            />
          </MDBox>
        </Grid>
        {/* <Grid item xs={12} md={6} lg={3}>
          <MDBox mb={1.5}>
            <ComplexStatisticsCard
              color="info"
              icon="groups"
              title="My Groups"
              count="N/A"
              percentage={{
                color: "info",
                amount: "",
                label: "active participation",
              }}
            />
          </MDBox>
        </Grid> */}
      </Grid>
    </MDBox>
  );
}
